import { create } from 'zustand'

/**
 * Thème de l'interface : « light », « dark » ou « system » (suit le réglage de l'appareil).
 * Le choix est gardé dans le navigateur. Le thème appliqué est posé sur <html data-theme>,
 * que lisent styles/tokens.css et Tailwind. index.html fait la même chose avant le premier
 * rendu pour éviter un flash : garder STORAGE_KEY et la logique identiques des deux côtés.
 */
export const STORAGE_KEY = 'jlp-theme'
export const THEME_PREFERENCES = ['light', 'dark', 'system']

const META_COLORS = { light: '#f8f9fc', dark: '#0a0a0a' }
const systemQuery = typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: light)') : null

function readPreference() {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    return THEME_PREFERENCES.includes(value) ? value : 'system'
  } catch {
    return 'system'
  }
}

function resolve(preference) {
  if (preference !== 'system') return preference
  return systemQuery?.matches ? 'light' : 'dark'
}

function apply(theme) {
  const root = document.documentElement
  root.dataset.theme = theme
  root.style.colorScheme = theme
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', META_COLORS[theme])
}

export const useThemeStore = create((set, get) => ({
  preference: readPreference(),
  theme: resolve(readPreference()),

  setPreference: (preference) => {
    if (!THEME_PREFERENCES.includes(preference)) return
    try {
      localStorage.setItem(STORAGE_KEY, preference)
    } catch {
      // Stockage indisponible (navigation privée…) : le choix vaut pour la session.
    }
    const theme = resolve(preference)
    apply(theme)
    set({ preference, theme })
  },

  /** Réapplique le thème quand l'appareil change de mode, si l'utilisateur a choisi « Automatique ». */
  syncWithSystem: () => {
    if (get().preference !== 'system') return
    const theme = resolve('system')
    apply(theme)
    set({ theme })
  },
}))

apply(useThemeStore.getState().theme)
systemQuery?.addEventListener('change', () => useThemeStore.getState().syncWithSystem())
// Un autre onglet a changé le thème.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) useThemeStore.getState().setPreference(readPreference())
  })
}
